/* eslint-disable default-param-last */
import PropTypes from 'prop-types'
import React from 'react'
import * as MessageApi from '../services/api/messagesApi'
import * as ModelsApi from '../services/api/modelsApi'

const SentMessagesStateContext = React.createContext()
const SentMessagesDispatchContext = React.createContext()

const initialState = {
  sentMessages: [],
  messageModels: [],
  fileName: '',
  fileNameHeader: '',
  destinataries: 0,
  fileUploadedError: [],
  fileUploadedHeaderError: [],
  pageNumber: 1,
  totalPages: 1,
  itemsPerPage: 10,
  loading: false,
  error: false,
  settings: { loading: false },
}

const sendMessagesReducer = (state = {}, action) => {
  switch (action.type) {
    case 'GET':
      return { ...state, sentMessages: [], loading: true }
    case 'GET_RECEIVED':
      return {
        ...state,
        sentMessages: action.sentMessages,
        totalPages: action.totalPages,
        pageNumber: action.pageNumber,
        itemsPerPage: action.itemsPerPage,
        loading: false,
      }
    case 'GET_REQUEST_FAILURE':
      return {
        ...state,
        sentMessages: [],
        loading: false,
        openMessage: true,
        message: 'Falha ao buscar modelos',
        error: true,
      }
    case 'GET_MODELS':
      return { ...state, messageModels: [] }
    case 'GET_MODELS_RECEIVED':
      return { ...state, messageModels: action.messageModels }
    case 'GET_MODELS_FAILURE':
      return {
        ...state,
        messageModels: [],
        loading: false,
        openMessage: true,
        message: 'Falha ao listar modelos de mensagens cadastrados',
      }
    case 'ADD':
      return { ...state }
    case 'ADD_SUCCESS':
      return {
        ...state,
        openMessage: true,
        message: 'Sua mensagem foi enviada',
        destinataries: 0,
        fileUploadedError: [],
        fileUploadedHeaderError: [],
        spreadsheetId: null,
        fileId: null,
        fileNameHeader: null,
        fileType: null,
      }
    case 'ADD_FAILURE':
      return {
        ...state,
        openMessage: true,
        message: 'Falha ao enviar mensagem',
        destinataries: 0,
        fileUploadedError: [],
        fileUploadedHeaderError: [],
        spreadsheetId: null,
        fileId: null,
        fileNameHeader: null,
        fileType: null,
      }
    case 'UPLOAD':
      return { ...state, fileName: action.fileName, loading: true, fileUploadedError: [] }
    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        destinataries: action.destinataries,
        spreadsheetId: action.spreadsheetId,
        loading: false,
        fileUploadedError: [],
      }
    case 'UPLOAD_FAILURE':
      return { ...state, fileUploadedError: action.errors, fileName: '', destinataries: 0, loading: false }
    case 'CANCEL_UPLOAD':
      return { ...state, fileName: '', destinataries: 0, fileUploadedError: [], spreadsheetId: '', loading: false }
    case 'CLOSE_MESSAGE':
      return { ...state, openMessage: false }
    case 'UPLOAD_HEADER':
      return { ...state, fileNameHeader: action.fileNameHeader, loading: true, fileUploadedHeaderError: [] }
    case 'UPLOAD_HEADER_SUCCESS':
      return {
        ...state,
        loading: false,
        fileId: action.fileId,
        fileNameHeader: action.fileNameHeader,
        fileType: action.fileType,
        fileUploadedHeaderError: [],
      }
    case 'UPLOAD_HEADER_FAILURE':
      return { ...state, fileUploadedHeaderError: action.errors, fileNameHeader: '', loading: false }
    case 'CANCEL_HEADER_UPLOAD':
      return { ...state, fileNameHeader: '', fileUploadedHeaderError: [], fileId: '', loading: false }
    case 'GET_SETTINGS':
      return { ...state, settings: { ...action.settings, loading: true } }
    case 'GET_SETTINGS_RECEIVED':
      return {
        ...state,
        settings: { ...action.settings, loading: false },
        loading: false,
      }
    case 'GET_SETTINGS_FAILURE':
      return {
        ...state,
        settings: { ...action.settings, loading: false },
        loading: false,
        error: true,
      }
    default:
      return { ...state, ...initialState }
  }
}

function SentMessagesProvider({ children }) {
  const [state, dispatch] = React.useReducer(sendMessagesReducer, { ...initialState })
  return (
    <SentMessagesStateContext.Provider value={state}>
      <SentMessagesDispatchContext.Provider value={dispatch}>{children}</SentMessagesDispatchContext.Provider>
    </SentMessagesStateContext.Provider>
  )
}

function getModels(dispatch) {
  dispatch({ type: 'GET_MODELS' })
  ModelsApi.getAll()
    .then((data) => {
      dispatch({ type: 'GET_MODELS_RECEIVED', messageModels: data.data })
    })
    .catch(() => {
      dispatch({ type: 'GET_MODELS_FAILURE' })
    })
}

function sendMessage(dispatch, payload) {
  return MessageApi.create(payload)
    .then(() => {
      dispatch({ type: 'ADD_SUCCESS' })
    })
    .catch(() => {
      dispatch({ type: 'ADD_FAILURE' })
    })
}

function sendMessageMeta(dispatch, payload) {
  return MessageApi.send(payload)
    .then(() => {
      dispatch({ type: 'ADD_SUCCESS' })
    })
    .catch(() => {
      dispatch({ type: 'ADD_FAILURE' })
    })
}

function uploadFile(dispatch, payload) {
  dispatch({ type: 'UPLOAD_HEADER', fileNameHeader: payload.name })
  const formData = new FormData()
  formData.append('file', payload, payload.name)

  MessageApi.uploadHeaderFile(formData)
    .then((data) => {
      if (data.data.validations) {
        dispatch({ type: 'UPLOAD_HEADER_FAILURE', errors: data.data.validations })
      } else {
        dispatch({
          type: 'UPLOAD_HEADER_SUCCESS',
          fileId: data.data,
          fileNameHeader: payload.name,
          fileType: payload.type,
        })
      }
    })
    .catch((error) => {
      if ('message' in error) {
        dispatch({ type: 'UPLOAD_HEADER_FAILURE', errors: [error.message] })
      } else {
        dispatch({ type: 'UPLOAD_HEADER_FAILURE', errors: error.data.validations })
      }
    })
}

function uploadSpreadsheet(dispatch, payload) {
  dispatch({ type: 'UPLOAD', fileName: payload.file.name })
  const formData = new FormData()
  formData.append('file', payload.file, payload.file.name)

  MessageApi.uploadFile(formData, payload.templateid)
    .then((data) => {
      if (data.data.validations) {
        dispatch({ type: 'UPLOAD_FAILURE', errors: data.data.validations })
      } else {
        dispatch({
          type: 'UPLOAD_SUCCESS',
          destinataries: data.data.recipients,
          spreadsheetId: data.data.spreadsheetId,
        })
      }
    })
    .catch((response) => {
      if (response.data?.validations) {
        dispatch({ type: 'UPLOAD_FAILURE', errors: response.data.validations })
      } else {
        dispatch({
          type: 'UPLOAD_FAILURE',
          errors: [{ message: 'Ocorreu um erro ao tentar realizar o upload deste arquivo.' }],
        })
      }
    })
}

function cancelUpload(dispatch, errors) {
  if (errors) dispatch({ type: 'UPLOAD_FAILURE', errors })
  else dispatch({ type: 'CANCEL_UPLOAD' })
}

function cancelHeaderUpload(dispatch) {
  dispatch({ type: 'CANCEL_HEADER_UPLOAD' })
}

function closeMessage(dispatch) {
  dispatch({ type: 'CLOSE_MESSAGE' })
}

function getSettings(dispatch) {
  dispatch({ type: 'GET_SETTINGS' })
  ModelsApi.getSettings()
    .then((data) => {
      dispatch({
        type: 'GET_SETTINGS_RECEIVED',
        settings: data.data,
      })
    })
    .catch(() => {
      dispatch({ type: 'GET_SETTINGS_FAILURE' })
    })
}

function useSentMessagesDispatch() {
  const context = React.useContext(SentMessagesDispatchContext)
  if (context === undefined) {
    throw new Error(`SentMessagesDispatchContext must be used within a SentMessagesProvider`)
  }
  return context
}

function useSentMessagesState() {
  const context = React.useContext(SentMessagesStateContext)
  if (context === undefined) {
    throw new Error(`SentMessagesStateContext must be used within a SentMessagesProvider`)
  }
  return context
}

SentMessagesProvider.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
}

export {
  cancelHeaderUpload,
  cancelUpload,
  closeMessage,
  getModels,
  getSettings,
  sendMessage,
  sendMessageMeta,
  sendMessagesReducer,
  SentMessagesProvider,
  uploadFile,
  uploadSpreadsheet,
  useSentMessagesDispatch,
  useSentMessagesState,
}
